import Box from '@mui/material/Box';
import PropTypes from 'prop-types'; // @mui
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { useSettingsContext } from 'src/components/settings';
//
import { Card, CardContent, Link, Stack, Typography, useTheme } from '@mui/material';
import AnnouncementBanner from 'src/components/announcement-banner';
import { useOrganizationContext } from 'src/context/organization';
import { useEffectiveRole } from 'src/hooks/use-effective-role';
import { usePathname } from 'src/routes/hook';
import { isAssignedTasksPath, isWebCallingLocked } from 'src/utils/assigned-tasks/calling-access';
import { isLockExemptPath } from 'src/utils/organization-helper';
import { HEADER, NAV } from '../config-layout';

// ----------------------------------------------------------------------

const SPACING = 8;

/** A white veil over the whole page, with the message card on top. Blocks every click beneath. */
function LockOverlay({ children }) {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(255,255,255,0.9)',
        zIndex: 1200,
        alignItems: 'center',
        pt: '40vh',
      }}
    >
      <Card elevation={3} sx={{ border: `2px solid ${theme.palette.primary.main}` }}>
        <CardContent>
          <Stack gap={1}>{children}</Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

LockOverlay.propTypes = {
  children: PropTypes.node,
};

export default function Main({ children, sx, ...other }) {
  const settings = useSettingsContext();
  const { isOrgActive, selectedOrganizationId, selectedOrgData } = useOrganizationContext();
  const effectiveRole = useEffectiveRole();
  const pathname = usePathname();

  // An expired or deactivated org is locked out of every screen EXCEPT Billing — the one place
  // it can see what it owes and renew. Locking that too would leave the message below with no
  // way to act on it. See isLockExemptPath, which the nav reads as well.
  const isLocked = !isOrgActive && !isLockExemptPath(pathname);

  // An org not in WEB_CALLING_ENABLED_ORG_IDS is locked out of Assigned Tasks. Unlike the
  // membership lock the page is not rendered beneath the veil at all, so its tasks are neither
  // fetched nor reachable by keyboard. The call session sits outside this layout and redirects
  // here instead (pages/assigned-tasks/session). Volunteers never meet it: EffectiveRoleGuard
  // sends them to voter search, and drawing the card would only flash it before that.
  const isCallingLocked =
    !isLocked &&
    effectiveRole !== 'user' &&
    isAssignedTasksPath(pathname) &&
    isWebCallingLocked(selectedOrganizationId);

  const lgUp = useResponsive('up', 'lg');

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  if (isNavHorizontal) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: `${HEADER.H_MOBILE + 24}px`,
          pb: 10,
          ...(lgUp && {
            pt: `${HEADER.H_MOBILE * 2 + 40}px`,
            pb: 15,
          }),
        }}
      >
        <AnnouncementBanner />
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        flexGrow: 1,
        py: `${HEADER.H_MOBILE + SPACING}px`,
        ...(lgUp && {
          px: 2,
          py: `${HEADER.H_DESKTOP + SPACING}px`,
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {isLocked && (
        <LockOverlay>
          <Typography color="text.secondary">
            Your organization is past its membership date.
          </Typography>
          <Typography color="text.secondary">
            To renew, contact{' '}
            <Link href={'mailto:info@rightinsight.org'} target={'_blank'}>
              info@rightinsight.org
            </Link>
            .
          </Typography>
        </LockOverlay>
      )}

      {isCallingLocked && (
        <LockOverlay>
          <Typography color="text.secondary">
            Calling from the web is not enabled for{' '}
            {selectedOrgData?.name ? <strong>{selectedOrgData.name}</strong> : 'your organization'}.
          </Typography>
          <Typography color="text.secondary">
            To turn it on, contact{' '}
            <Link href={'mailto:info@rightinsight.org'} target={'_blank'}>
              info@rightinsight.org
            </Link>
            .
          </Typography>
        </LockOverlay>
      )}

      <AnnouncementBanner />
      {!isCallingLocked && children}
    </Box>
  );
}

Main.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};
